<template>
  <UIInputDynamicList
    v-model="selectedFilterId"
    :data="values"
    :container
    removable
    editable
    size="xs"
    :placeholder="isMobile ? 'Saved filters...' : 'Select saved filter or create new...'"
    class="w-full max-w-29.5 sm:max-w-54.5 [&_.ui-input-text__input]:rounded-md!"
    @click:new="handleCreate"
    @update="handleUpdate"
    @delete="handleDelete"
    @update:model-value="handleApply"
  />
</template>

<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue'
import { cloneDeep, isEqual } from 'lodash'

import { generateRandomKey } from '@/helpers/common'

import { useResponsive } from '@/plugins/responsiveUI'

import { useRepositoriesStore } from '@/store/repositories'

import { Filter } from '@types'

import { UIInputDynamicList } from '@ui/fields'

type SavedFilter = {
  id: string
  name: string
  data: Filter
}

type Props = {
  filterKey: string
}

const props = defineProps<Props>()

const container = inject<string>('container')

const modelValue = defineModel<Filter>()

const repositoryStore = useRepositoriesStore()

const { isMobile } = useResponsive()

const selectedFilterId = ref()

const filters = computed({
  get() {
    return repositoryStore.getCurrentUISettings?.savedFilters?.[props.filterKey] || []
  },
  set(value) {
    repositoryStore.setCurrentUISettings([[`savedFilters.${props.filterKey}`, value]])
  }
})

const values = computed(() => filters.value.map((filter: SavedFilter) => ({
  value: filter.id,
  label: filter.name,
})))

const handleApply = (id?: string) => {
  const filter = filters.value.find((filter: SavedFilter) => filter.id === id)
  if (!filter) return
  modelValue.value = cloneDeep(filter.data)
}

const handleCreate = (name: string, callback: () => void) => {
  const id = generateRandomKey()
  filters.value = [...filters.value, {
    id,
    name,
    data: cloneDeep(modelValue.value),
  }]
  selectedFilterId.value = id
  callback()
}

const handleUpdate = (id: string, value: string) => {
  filters.value = filters.value.map((filter: SavedFilter) => filter.id === id ? { ...filter, name: value } : filter)
}

const handleDelete = (id: string, callback: () => void) => {
  filters.value = filters.value.filter((filter: SavedFilter) => filter.id !== id)
  callback()
}

watch(modelValue, value => {
  selectedFilterId.value = filters.value.find((filter: SavedFilter) => isEqual(filter.data, value))?.id
}, { immediate: true })
</script>
